import { useState, useCallback } from 'react';

export interface UseClipboardOptions {
  timeout?: number;
}

export interface UseClipboardReturn {
  copy: (text: string) => Promise<boolean>;
  copied: boolean;
  error: string | null;
  isSupported: boolean;
}

/**
 * Custom hook for copying text to clipboard
 * Provides fallback for browsers without Clipboard API
 */
export function useClipboard(options: UseClipboardOptions = {}): UseClipboardReturn {
  const { timeout = 2000 } = options;
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if Clipboard API is supported
  const isSupported = Boolean(
    typeof navigator !== 'undefined' && 
    navigator.clipboard && 
    navigator.clipboard.writeText
  );

  // Fallback method for older browsers
  const fallbackCopy = useCallback((text: string): boolean => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return successful;
    } catch (err) {
      console.error('Fallback copy failed:', err);
      return false;
    }
  }, []);

  const copy = useCallback(async (text: string): Promise<boolean> => {
    if (!text) {
      setError('No text provided to copy');
      return false;
    }

    setError(null);

    try {
      if (isSupported) {
        await navigator.clipboard.writeText(text);
      } else {
        const success = fallbackCopy(text);
        if (!success) {
          throw new Error('Fallback copy method failed');
        }
      }

      setCopied(true);
      
      // Reset copied state after timeout
      setTimeout(() => setCopied(false), timeout);
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to copy to clipboard';
      setError(errorMessage);
      console.error('Copy to clipboard failed:', err);
      return false;
    }
  }, [isSupported, fallbackCopy, timeout]);

  return {
    copy,
    copied,
    error,
    isSupported,
  };
}